/**
 * Temperature Role - Convert Pillar
 *
 * Conversão entre unidades de temperatura usando arquitetura hub-and-spoke.
 * Base: celsius (°C)
 *
 * IMPORTANTE: Diferente de outras roles, temperatura usa FÓRMULAS
 * ao invés de fatores multiplicativos (escalas têm offsets diferentes).
 *
 * Fórmulas de conversão:
 * - Celsius ↔ Fahrenheit: °F = °C × 9/5 + 32
 * - Celsius ↔ Kelvin: K = °C + 273.15
 * - Celsius ↔ Rankine: °R = (°C + 273.15) × 9/5
 *
 * @example
 * import { convertTemperature, toBaseTemperature, fromBaseTemperature } from '@attrx/role-morphic/temperature/convert';
 *
 * convertTemperature('celsius', 'fahrenheit', 0);   // 32
 * convertTemperature('celsius', 'kelvin', 0);       // 273.15
 * toBaseTemperature('fahrenheit', 32);              // 0
 * fromBaseTemperature('kelvin', 0);                 // 273.15
 */

import { TEMPERATURE_UNITS, type TemperatureUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const TEMPERATURE_BASE: TemperatureUnit = "celsius";

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte valor para a unidade base (celsius)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em celsius
 *
 * @example
 * toBaseTemperature('fahrenheit', 32);   // 0
 * toBaseTemperature('kelvin', 273.15);   // 0
 * toBaseTemperature('rankine', 491.67);  // 0
 */
export function toBaseTemperature(variant: TemperatureUnit, value: number): number {
  const unit = TEMPERATURE_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown temperature variant: ${variant}`);
  }

  // Temperatura usa fórmulas (toBase/fromBase), não factor
  if (unit.toBase) {
    return unit.toBase(value);
  }

  // Fallback (não deveria acontecer para temperature)
  return value * (unit.factor ?? 1);
}

/**
 * Converte valor da unidade base (celsius) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em celsius
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseTemperature('fahrenheit', 0);   // 32
 * fromBaseTemperature('kelvin', 0);       // 273.15
 * fromBaseTemperature('rankine', 0);      // 491.67
 */
export function fromBaseTemperature(variant: TemperatureUnit, baseValue: number): number {
  const unit = TEMPERATURE_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown temperature variant: ${variant}`);
  }

  // Temperatura usa fórmulas (toBase/fromBase), não factor
  if (unit.fromBase) {
    return unit.fromBase(baseValue);
  }

  // Fallback (não deveria acontecer para temperature)
  return baseValue / (unit.factor ?? 1);
}

/**
 * Converte entre duas unidades de temperatura
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertTemperature('celsius', 'fahrenheit', 0);     // 32
 * convertTemperature('celsius', 'fahrenheit', 100);   // 212
 * convertTemperature('fahrenheit', 'celsius', 32);    // 0
 * convertTemperature('celsius', 'kelvin', 0);         // 273.15
 * convertTemperature('kelvin', 'rankine', 0);         // 0
 */
export function convertTemperature(
  from: TemperatureUnit,
  to: TemperatureUnit,
  value: number
): number {
  // Otimização: mesma unidade
  if (from === to) {
    return value;
  }

  // Hub-and-spoke: from → base (celsius) → to
  const baseValue = toBaseTemperature(from, value);
  return fromBaseTemperature(to, baseValue);
}

/**
 * Versão safe de convertTemperature que retorna Result
 *
 * @example
 * tryConvertTemperature('celsius', 'fahrenheit', 0);
 * // { ok: true, value: 32 }
 *
 * tryConvertTemperature('invalid', 'fahrenheit', 0);
 * // { ok: false, error: 'Unknown temperature variant: invalid' }
 */
export function tryConvertTemperature(
  from: TemperatureUnit,
  to: TemperatureUnit,
  value: number
): Result<number> {
  try {
    const result = convertTemperature(from, to, value);
    return { ok: true, value: result };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Verifica se uma variante existe
 */
export function hasTemperatureVariant(variant: string): variant is TemperatureUnit {
  return variant in TEMPERATURE_UNITS;
}

/**
 * Lista todas as variantes disponíveis
 */
export function getTemperatureVariants(): TemperatureUnit[] {
  return Object.keys(TEMPERATURE_UNITS) as TemperatureUnit[];
}
